/// <reference types="vite/client" />

export type Platform = "ios" | "ipados" | "android" | "desktop";
export type SupportedBrowserKey = "chrome" | "edge" | "safari" | "samsung";
export type DeepLinkResult = "deeplinked" | "copied" | "failed";

export interface BrowserInfo {
  name: string;
  supported: boolean;
  platform: Platform;
  // LSP-17654: true when the page is inside another app's embedded WebView rather than a
  // standalone browser. The panel needs it because the NAME of an in-app browser is not
  // something the user can "switch to" — the remedy is "open this in Safari", not "use Facebook".
  isInAppBrowser: boolean;
}

function readUserAgent(): string {
  try {
    const userAgent = navigator.userAgent;
    return typeof userAgent === "string" ? userAgent : "";
  } catch {
    return "";
  }
}

export function isTouchCapableMac(navigatorLike?: Pick<Navigator, "platform" | "maxTouchPoints">): boolean {
  try {
    const deviceNavigator = navigatorLike ?? navigator;
    return deviceNavigator.platform === "MacIntel" && deviceNavigator.maxTouchPoints > 1;
  } catch {
    return false;
  }
}

function detectPlatformFromUserAgent(userAgent: string): Platform {
  // iPadOS Safari's default UA masquerades as desktop Safari (no "iPad" token), which is why
  // isTouchCapableMac() (MacIntel + multi-touch) is checked alongside the UA token — the same
  // detection DeviceUtility.isIpad uses in shared/frontend/meeting/src/helpers/device.ts.
  //
  // NOT FIXING: this also matches an iPhone with Safari's "Request Desktop Website" enabled (same
  // spoofed signature), which is then treated as iPadOS rather than iOS. Raised in review and
  // deliberately left as-is — detection here is by design and stays simple, with no screen-size or
  // other heuristic to disambiguate it. A user who opts into desktop-site mode is accepted as
  // desktop-class; do not "fix" this without a product decision to point at.
  if (/iPad/.test(userAgent) || isTouchCapableMac()) {
    return "ipados";
  }
  if (/iPhone|iPod/.test(userAgent)) {
    return "ios";
  }
  if (/android/i.test(userAgent)) {
    return "android";
  }
  return "desktop";
}

export function detectPlatform(): Platform {
  return detectPlatformFromUserAgent(readUserAgent());
}

/**
 * Detects Brave through the browser's own API. Desktop Brave deliberately uses a Chrome
 * user-agent, so UA parsing cannot distinguish it. Every failure returns false so browser
 * detection failures never prevent a user from continuing.
 */
export async function detectBrave(): Promise<boolean> {
  const detectionTimeoutMs = 1000;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    const brave = (
      navigator as Navigator & {
        brave?: { isBrave?: () => boolean | Promise<boolean> };
      }
    ).brave;
    if (typeof brave?.isBrave !== "function") return false;
    const result = await Promise.race([
      Promise.resolve(brave.isBrave()),
      new Promise<false>((resolve) => {
        timeoutId = setTimeout(() => resolve(false), detectionTimeoutMs);
      }),
    ]);
    return result === true;
  } catch {
    return false;
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

// LSP-17654: ordered most-specific-first, and every entry MUST be tested before the
// real-Safari check below. Some of these user-agents carry a `Safari/` token (Google app,
// DuckDuckGo) and DuckDuckGo also carries `Version/`, so the generic checks cannot
// distinguish them. Names are user-facing — they are rendered into "It looks like you're
// using X".
const IN_APP_BROWSER_SIGNATURES: ReadonlyArray<{
  pattern: RegExp;
  name: string;
}> = [
  { pattern: /FBAN\/|FBAV\/|FB_IAB\/|FBIOS/i, name: "Facebook" },
  { pattern: /Instagram/i, name: "Instagram" },
  { pattern: /LinkedInApp/i, name: "LinkedIn" },
  { pattern: /MicroMessenger/i, name: "WeChat" },
  { pattern: /\bLine\//i, name: "LINE" },
  { pattern: /Snapchat/i, name: "Snapchat" },
  { pattern: /GSA\//i, name: "the Google app" },
];

// LSP-17654: shared by the iOS and Android branches below — both need "which host app's
// signature, if any, matched this UA" against the same table.
function findInAppSignature(ua: string) {
  return IN_APP_BROWSER_SIGNATURES.find((signature) =>
    signature.pattern.test(ua),
  );
}

// Real mobile Safari always emits a `Version/<n>` token alongside `Safari/`; embedded
// WKWebViews generally do not. This is the only discriminator available from the
// user-agent, and its absence is why the previous `/Safari\//` test matched in-app browsers.
const IOS_REAL_SAFARI = /Version\/[\d.]+.*Safari\//i;

// LSP-17654: Brave for iOS ships a default `Brave/` token
// (https://github.com/brave/brave-browser/issues/43895). BEST EFFORT ONLY — Brave suppresses
// the token on a host-exception list, so a Brave user on an excepted host presents as plain
// mobile Safari and is indistinguishable. Never treat the ABSENCE of this token as proof the
// browser is not Brave. Brave is a standalone browser, not an embedded WebView, so it is a
// branch of its own rather than an entry in the in-app table above.
const IOS_BRAVE = /\bBrave\//i;

// F3 (PR review): DuckDuckGo's iOS/Android apps are standalone browsers, not embedded
// WebViews — they used to be a IN_APP_BROWSER_SIGNATURES entry, which wrongly rendered
// "DuckDuckGo's in-app browser". A standalone-browser branch of its own, same shape as Brave.
const IOS_DUCKDUCKGO = /DuckDuckGo\//i;

// Used when we know the page is inside SOME embedded WebView but cannot name the host app.
// Deliberately not a browser name: naming the wrong browser is the defect being fixed.
const GENERIC_IN_APP_BROWSER_NAME = "an in-app browser";

/**
 * Detects the current browser and whether it is supported.
 * Pass `supportedOverrides` to change the default support status for specific browsers
 * (keyed by browser name, e.g. `{ "Samsung Internet": false }`).
 */
export function detectBrowser(
  supportedOverrides?: Record<string, boolean>,
): BrowserInfo {
  const ua = readUserAgent();
  const platform = detectPlatformFromUserAgent(ua);
  let result: BrowserInfo;

  if (platform === "ios" || platform === "ipados") {
    // Same WebKit UA tokens on both platforms (CriOS/FxiOS/EdgiOS/etc. don't vary by
    // iPhone vs iPad), so iPadOS reuses the iOS engine-detection branch verbatim.
    const inAppSignature = findInAppSignature(ua);
    // LSP-17654: the OLD branch ended in `else if (/Safari\//i.test(ua)) -> supported: true`
    // and `else -> supported: false`. contact-async's checkBrowserSupport() reads ONLY
    // `.supported` to decide whether to block a capture, so this fix must not move any UA
    // across that line. Preserving the old predicate verbatim is what keeps this change
    // additive for that consumer — see the isolation note in the plan.
    const legacySupported = /Safari\//i.test(ua);
    if (/CriOS\//i.test(ua))
      result = {
        name: "Chrome",
        supported: true,
        platform,
        isInAppBrowser: false,
      };
    else if (/FxiOS\//i.test(ua))
      result = {
        name: "Firefox",
        supported: false,
        platform,
        isInAppBrowser: false,
      };
    else if (/EdgiOS\//i.test(ua))
      result = {
        name: "Edge",
        supported: true,
        platform,
        isInAppBrowser: false,
      };
    else if (/OPiOS\//i.test(ua))
      result = {
        name: "Opera",
        supported: false,
        platform,
        isInAppBrowser: false,
      };
    else if (IOS_BRAVE.test(ua))
      result = {
        name: "Brave",
        supported: legacySupported,
        platform,
        isInAppBrowser: false,
      };
    else if (IOS_DUCKDUCKGO.test(ua))
      result = {
        name: "DuckDuckGo",
        supported: legacySupported,
        platform,
        isInAppBrowser: false,
      };
    else if (inAppSignature)
      result = {
        name: inAppSignature.name,
        supported: legacySupported,
        platform,
        isInAppBrowser: true,
      };
    else if (IOS_REAL_SAFARI.test(ua))
      result = {
        name: "Safari",
        supported: true,
        platform,
        isInAppBrowser: false,
      };
    else
      // F9 (PR review): this is a best-effort default, not a certainty — a standalone iOS
      // browser that doesn't emit the Version/...Safari/ combo (Firefox Focus, Opera Touch,
      // Yandex Browser are known examples) is misclassified as in-app here too. There is no
      // further UA signal available to distinguish them; documented rather than silently
      // assumed correct, same spirit as IOS_BRAVE's host-exception caveat above.
      result = {
        name: GENERIC_IN_APP_BROWSER_NAME,
        supported: legacySupported,
        platform,
        isInAppBrowser: true,
      };
  } else if (platform === "android") {
    // LSP-17654: Android WebViews carry the `; wv)` token — the same check DeviceUtility
    // uses (shared/frontend/meeting/src/helpers/device.ts:80-90).
    const isAndroidWebView = /;\s*wv\)/.test(ua);
    // Android WebView exposes its Chromium engine in the UA, so calling a bare WebView
    // "Chrome's in-app browser" falsely tells the user which host opened the page. Prefer a
    // known host-app signature; otherwise use the same non-committal in-app description as iOS.
    // The support value below remains derived from the legacy engine checks so contact-async's
    // support gate sees exactly the same result for every Android UA.
    const inAppSignature = findInAppSignature(ua);
    const androidInAppName =
      inAppSignature?.name ??
      (isAndroidWebView ? GENERIC_IN_APP_BROWSER_NAME : null);
    const isInAppBrowser = isAndroidWebView || Boolean(inAppSignature);
    // F1 (PR review): supportedOverrides is keyed by name, and androidInAppName can rename the
    // result out from under a caller's override key (e.g. contact-async's {"Samsung Internet":
    // false} silently stops matching once a GSA-in-Samsung-engine UA is renamed to "the Google
    // app"). androidEngineName keeps the pre-rename engine identity so overrides can still be
    // applied by the name a caller would reasonably expect, independent of the display name.
    let androidEngineName: string;
    if (/EdgA?\//i.test(ua)) androidEngineName = "Edge";
    else if (/OPR\//i.test(ua) || /Opera/i.test(ua))
      androidEngineName = "Opera";
    else if (/SamsungBrowser\//i.test(ua))
      androidEngineName = "Samsung Internet";
    else if (/Firefox\//i.test(ua)) androidEngineName = "Firefox";
    else if (/Chrome\//i.test(ua)) androidEngineName = "Chrome";
    else androidEngineName = "Unknown";
    const androidSupported =
      androidEngineName === "Edge" ||
      androidEngineName === "Samsung Internet" ||
      androidEngineName === "Chrome";
    // PR review ("Android DuckDuckGo name lost"): the F3 fix removed DuckDuckGo from the shared
    // IN_APP_BROWSER_SIGNATURES table (it is a standalone browser, not a WebView host) but only
    // added a standalone-name branch for iOS. Android DuckDuckGo UAs carry `DuckDuckGo/` on top of
    // `Chrome/`, so without this they fell through to the bare androidEngineName and displayed as
    // "Chrome". androidEngineName itself is intentionally left resolving to "Chrome" here — it is
    // the underlying rendering engine and supportedOverrides/androidSupported must keep keying off
    // it (see the F1 comment above) — only the display name changes for this standalone browser.
    const isAndroidDuckDuckGo = !isInAppBrowser && /DuckDuckGo\//i.test(ua);
    const androidDisplayName =
      androidInAppName ??
      (isAndroidDuckDuckGo ? "DuckDuckGo" : androidEngineName);
    if (supportedOverrides && androidEngineName in supportedOverrides) {
      return {
        name: androidDisplayName,
        supported: supportedOverrides[androidEngineName],
        platform,
        isInAppBrowser,
      };
    }
    result = {
      name: androidDisplayName,
      supported: androidSupported,
      platform,
      isInAppBrowser,
    };
  } else {
    if (/Edg\//i.test(ua))
      result = {
        name: "Edge",
        supported: true,
        platform,
        isInAppBrowser: false,
      };
    else if (/OPR\//i.test(ua) || /Opera/i.test(ua))
      result = {
        name: "Opera",
        supported: false,
        platform,
        isInAppBrowser: false,
      };
    else if (/Firefox\//i.test(ua))
      result = {
        name: "Firefox",
        supported: false,
        platform,
        isInAppBrowser: false,
      };
    else if (/Chrome\//i.test(ua))
      result = {
        name: "Chrome",
        supported: true,
        platform,
        isInAppBrowser: false,
      };
    else if (/Safari\//i.test(ua))
      result = {
        name: "Safari",
        supported: true,
        platform,
        isInAppBrowser: false,
      };
    else if (/Trident/i.test(ua) || /MSIE/i.test(ua))
      result = {
        name: "Internet Explorer",
        supported: false,
        platform,
        isInAppBrowser: false,
      };
    else
      result = {
        name: "Unknown",
        supported: false,
        platform,
        isInAppBrowser: false,
      };
  }

  if (supportedOverrides && result.name in supportedOverrides) {
    result.supported = supportedOverrides[result.name];
  }

  return result;
}

export function getAppUrl(): string {
  return window.location.href;
}

export function getAppHostPath(): string {
  return (
    window.location.host +
    window.location.pathname +
    window.location.search +
    window.location.hash
  );
}

/**
 * Returns host + pathname + search with the hash fragment moved to a `_hash`
 * query parameter. Android intent URLs reserve `#` for the `#Intent;`
 * separator, so including `window.location.hash` directly would produce a
 * malformed double-`#` URI. Encoding it as a query param preserves session
 * data (e.g. roomKey) through the intent redirect.
 */
export function getIntentHostPath(): string {
  const base = window.location.host + window.location.pathname;
  const search = encodeLinkEndingCharacters(window.location.search);
  const hash = window.location.hash;

  if (!hash) return base + search;

  const separator = search ? "&" : "?";
  return (
    base +
    search +
    separator +
    "_hash=" +
    encodeLinkEndingCharacters(encodeURIComponent(hash.substring(1)))
  );
}

/**
 * LSP-21622: `! ' ( ) *` are legal unencoded in a query (encodeURIComponent and Vue Router both
 * leave them raw), but a hand-off can end the URL at them — a customer sent from Samsung Internet
 * to Chrome arrived with the conversation token cut at its `!!` and every upload was refused.
 * Percent-encoding them changes nothing for the receiving page: URLSearchParams decodes both forms
 * to the same value.
 */
function encodeLinkEndingCharacters(value: string): string {
  return value.replace(
    /[!'()*]/g,
    (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`,
  );
}

/**
 * Attempts to open a URL in an iOS app via its custom URL scheme.
 * If the page goes hidden within the timeout, the app opened successfully and the
 * App Store fallback is cancelled and the promise resolves "deeplinked". If the page
 * stays visible, the user is redirected to the App Store and the promise resolves
 * "failed" — callers must not treat this as a confirmed hand-off (PR review: "Optimistic
 * deeplink clears recovery hint" — a caller was clearing a persistent recovery hint on
 * every "deeplinked" result, including this fire-and-forget case).
 */
export function openIosAppWithFallback(
  appUrl: string,
  appStoreUrl: string,
): Promise<DeepLinkResult> {
  return new Promise((resolve) => {
    let settled = false;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (fallbackTimer !== null) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", succeed);
    };

    const succeed = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve("deeplinked");
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") succeed();
    };

    fallbackTimer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      window.location.href = appStoreUrl;
      resolve("failed");
    }, 1500);

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", succeed);

    window.location.href = appUrl;
  });
}

/**
 * Attempts an Android `intent://` deep link. Android resolves the intent itself — if no
 * app is installed, the intent's own `S.browser_fallback_url` redirects the OS natively,
 * with no JS signal either way. Mirrors openIosAppWithFallback/tryDesktopDeepLink's
 * visibility-based confirmation so a caller can tell "the WebView actually switched away"
 * from "nothing observably happened yet" (PR review: "Optimistic deeplink clears recovery
 * hint" — resolving "deeplinked" unconditionally let a caller clear a persistent recovery
 * hint even when the intent silently did nothing).
 */
function tryAndroidIntentDeepLink(intentUrl: string): Promise<DeepLinkResult> {
  return new Promise((resolve) => {
    let settled = false;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

    // codex break-it round 2: cleanup() only removed listeners, never the pending fallback timer —
    // a confirmed hand-off (succeed() via pagehide) left its 1500ms timeout running harmlessly to
    // completion instead of being cancelled. fail()'s `settled` guard made this inert, but rapid
    // taps across Chrome/Edge/Samsung could leave several stale timers alive at once.
    const cancelFallbackTimer = () => {
      if (fallbackTimer !== null) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
    };

    const succeed = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve("deeplinked");
    };

    const fail = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve("failed");
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") succeed();
    };

    const cleanup = () => {
      cancelFallbackTimer();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", succeed);
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", succeed);

    window.location.href = intentUrl;

    fallbackTimer = setTimeout(fail, 1500);
  });
}

/**
 * Attempts a desktop deep link via `window.location.href`. If the target app
 * opens, the browser loses focus (blur) or the page becomes hidden, and we
 * resolve `"deeplinked"`. If neither fires within the timeout the scheme was
 * likely blocked, so we resolve `"failed"` and let the UI offer a copy button.
 */
export function tryDesktopDeepLink(
  deepLinkUrl: string,
): Promise<DeepLinkResult> {
  return new Promise((resolve) => {
    let settled = false;

    const succeed = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve("deeplinked");
    };

    const fail = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve("failed");
    };

    const onBlur = () => succeed();
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") succeed();
    };

    const cleanup = () => {
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };

    window.addEventListener("blur", onBlur);
    document.addEventListener("visibilitychange", onVisibilityChange);

    window.location.href = deepLinkUrl;

    setTimeout(fail, 1500);
  });
}

/**
 * Returns true only when the URL demonstrably reached the clipboard.
 *
 * LSP-17654: this used to return void and ignore document.execCommand's boolean, so a caller
 * had no way to know it had failed — and the unsupported-browser panel told the user "we've
 * copied the link, open it in Safari" even when both mechanisms failed, which on an in-app
 * browser (where clipboard permission is exactly what gets denied) leaves them with no way
 * forward and no idea why.
 */
export async function copyCurrentUrlToClipboard(): Promise<boolean> {
  const url = getAppUrl();
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch (clipboardError) {
    // PR review (CLAUDE.md "Diagnostic logging when fixing silent failures"): the UI's generic
    // "couldn't copy" text can't distinguish which of the three failure conditions below
    // occurred, so each one is logged individually here.
    console.warn(
      "copyCurrentUrlToClipboard: navigator.clipboard.writeText rejected, falling back to execCommand",
      clipboardError,
    );
    let textArea: HTMLTextAreaElement | null = null;
    try {
      textArea = document.createElement("textarea");
      textArea.value = url;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      // execCommand returns false when the copy was refused, and throws in some embedded
      // WebViews. Both are failures and must be reported as such.
      const copied = document.execCommand?.("copy") === true;
      if (!copied) {
        console.warn(
          "copyCurrentUrlToClipboard: document.execCommand('copy') returned false",
        );
      }
      return copied;
    } catch (execCommandError) {
      console.warn(
        "copyCurrentUrlToClipboard: document.execCommand('copy') threw",
        execCommandError,
      );
      return false;
    } finally {
      // finally, not a trailing statement: an execCommand throw used to leak the textarea
      // into the DOM permanently.
      try {
        textArea?.parentNode?.removeChild(textArea);
      } catch (removeChildError) {
        // Cleanup must not change a failed copy attempt into a rejected promise.
        console.warn(
          "copyCurrentUrlToClipboard: textarea cleanup removeChild threw",
          removeChildError,
        );
      }
    }
  }
}

export async function openInChrome(
  platform: Platform,
): Promise<DeepLinkResult> {
  if (platform === "android") {
    const hostPath = getIntentHostPath();
    const fallbackUrl = encodeURIComponent(
      "https://play.google.com/store/apps/details?id=com.android.chrome",
    );
    return tryAndroidIntentDeepLink(
      `intent://${hostPath}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${fallbackUrl};end`,
    );
  } else if (platform === "ios" || platform === "ipados") {
    return openIosAppWithFallback(
      `googlechromes://${getAppHostPath()}`,
      "https://apps.apple.com/app/google-chrome/id535886823",
    );
  } else {
    return tryDesktopDeepLink(`googlechromes://${getAppHostPath()}`);
  }
}

export async function openInEdge(platform: Platform): Promise<DeepLinkResult> {
  if (platform === "android") {
    const hostPath = getIntentHostPath();
    const fallbackUrl = encodeURIComponent(
      "https://play.google.com/store/apps/details?id=com.microsoft.emmx",
    );
    return tryAndroidIntentDeepLink(
      `intent://${hostPath}#Intent;scheme=https;package=com.microsoft.emmx;S.browser_fallback_url=${fallbackUrl};end`,
    );
  } else if (platform === "ios" || platform === "ipados") {
    return openIosAppWithFallback(
      `microsoft-edge-https://${getAppHostPath()}`,
      "https://apps.apple.com/app/microsoft-edge/id1288723196",
    );
  } else {
    window.open(`microsoft-edge:${getAppUrl()}`, "_blank");
    return "deeplinked";
  }
}

/**
 * LSP-17654: the URL the Safari recommendation should LINK to.
 *
 * Deliberately the plain https page URL — byte-identical to what copyCurrentUrlToClipboard()
 * puts on the clipboard. The reported bug is that clicking the Safari button did nothing
 * while pasting the copied link into Safari worked; the cause is that the click navigated to
 * the private, undocumented `x-safari-https://` scheme (openInSafari below) rather than to
 * the meeting URL. Rendering this as a real <a href> gives the browser an ordinary link it
 * can act on, and gives the user something they can long-press and open in Safari.
 *
 * openInSafari() is intentionally NOT changed: it is also consumed by
 * apps/contact-async/src/components/UnsupportedBrowserPanel.vue:60, a screen this ticket has
 * no reproduction, automation, or acceptance criteria for.
 */
export function getSafariOpenHref(): string {
  return getAppUrl();
}

export async function openInSafari(
  platform: Platform,
): Promise<DeepLinkResult> {
  if (platform === "ios" || platform === "ipados") {
    const originalUrl = getAppUrl();
    return new Promise((resolve) => {
      let settled = false;

      const succeed = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve("deeplinked");
      };

      const fail = async () => {
        if (settled) return;
        settled = true;
        cleanup();
        try {
          await navigator.clipboard.writeText(originalUrl);
          resolve("copied");
        } catch {
          resolve("failed");
        }
      };

      const onVisibilityChange = () => {
        if (document.visibilityState === "hidden") succeed();
      };

      const cleanup = () => {
        document.removeEventListener("visibilitychange", onVisibilityChange);
        window.removeEventListener("pagehide", succeed);
      };

      document.addEventListener("visibilitychange", onVisibilityChange);
      window.addEventListener("pagehide", succeed);

      window.location.href = `x-safari-https://${getAppHostPath()}`;

      setTimeout(fail, 1500);
    });
  } else if (platform === "desktop") {
    window.open(`x-safari-https://${getAppHostPath()}`, "_blank");
    return "deeplinked";
  } else {
    await copyCurrentUrlToClipboard();
    return "copied";
  }
}

export async function openInSamsung(
  _platform: Platform,
): Promise<DeepLinkResult> {
  const hostPath = getIntentHostPath();
  const fallbackUrl = encodeURIComponent(
    "https://play.google.com/store/apps/details?id=com.sec.android.app.sbrowser",
  );
  return tryAndroidIntentDeepLink(
    `intent://${hostPath}#Intent;scheme=https;package=com.sec.android.app.sbrowser;S.browser_fallback_url=${fallbackUrl};end`,
  );
}

export const PLATFORM_RECOMMENDATIONS: Record<Platform, SupportedBrowserKey[]> =
  {
    // LSP-20775: no Edge on iOS — it's just a WebKit skin of Safari there, never a real alternative.
    // Same reasoning applies to iPadOS: Apple requires every iPadOS browser to use WebKit.
    ios: ["safari", "chrome"],
    ipados: ["safari", "chrome"],
    android: ["chrome", "samsung", "edge"],
    desktop: ["chrome", "edge", "safari"],
  };

export const PLATFORM_LABEL: Record<Platform, string> = {
  ios: "iOS",
  ipados: "iPadOS",
  android: "Android",
  desktop: "desktop",
};

export const SUPPORTED_BROWSER_LABEL: Record<SupportedBrowserKey, string> = {
  chrome: "Chrome",
  edge: "Edge",
  safari: "Safari",
  samsung: "Samsung",
};

// LSP-17654: the panel used to interpolate `browser.name` straight into its sentence, which
// printed raw internal values (including the literal "Unknown") and read as if the named app
// were a browser the user could keep using. Owning the whole sentence here keeps copy and
// detection together.
const BROWSER_NAME_TO_LABEL: Record<string, string> = {
  Unknown: "a browser we don't recognise",
};

// LSP-17654: describeDetectedBrowser/getRecommendationsFor are exported, so an external
// caller's BrowserInfo is not guaranteed well-formed the way detectBrowser()'s own output is.
// break-it round 1 covers a BrowserInfo whose property GETTERS throw (Object.defineProperties
// with a throwing get()), so each field is read defensively on its own — one throwing getter
// must not block reading the other two fields.
function toSafeBrowserInfo(browser: BrowserInfo): BrowserInfo {
  let name = "Unknown";
  try {
    const rawName = browser?.name;
    if (typeof rawName === "string" && rawName) name = rawName;
  } catch {
    // name getter threw — keep the "Unknown" default.
  }

  let platform: Platform = "desktop";
  try {
    const rawPlatform = browser?.platform;
    if (
      rawPlatform === "ios" ||
      rawPlatform === "ipados" ||
      rawPlatform === "android" ||
      rawPlatform === "desktop"
    ) {
      platform = rawPlatform;
    }
  } catch {
    // platform getter threw — keep the "desktop" default.
  }

  let isInAppBrowser = false;
  try {
    isInAppBrowser = browser?.isInAppBrowser === true;
  } catch {
    // isInAppBrowser getter threw — keep the "false" default.
  }

  return { name, platform, isInAppBrowser, supported: false };
}

export function describeDetectedBrowser(browser: BrowserInfo): string {
  const safeBrowser = toSafeBrowserInfo(browser);
  const browserName = safeBrowser.name;
  const platformLabel = PLATFORM_LABEL[safeBrowser.platform];
  if (safeBrowser.isInAppBrowser) {
    // F8 (PR review): detectBrowser()'s own android branch can never produce name === "Unknown"
    // together with isInAppBrowser === true — androidInAppName is only null when isAndroidWebView
    // is false, which also forces isInAppBrowser to false in that same branch. The "Unknown"-named
    // in-app case this guards against can only reach here via an externally-constructed
    // BrowserInfo (describeDetectedBrowser is exported), which toSafeBrowserInfo does not rule
    // out — hence normalising defensively rather than assuming detectBrowser()'s own invariant
    // holds. Normalise through the same label map the non-in-app branch uses, and drop the
    // possessive entirely when the result is already a generic noun phrase — "an in-app browser's
    // in-app browser" is the other way to get this wrong.
    const normalisedName = BROWSER_NAME_TO_LABEL[browserName] ?? browserName;
    const isAlreadyGenericPhrase =
      normalisedName === GENERIC_IN_APP_BROWSER_NAME ||
      normalisedName.startsWith("a ");
    const subject = isAlreadyGenericPhrase
      ? GENERIC_IN_APP_BROWSER_NAME
      : `${normalisedName}'s in-app browser`;
    return `It looks like you're using ${subject} on ${platformLabel}.`;
  }
  const label = BROWSER_NAME_TO_LABEL[browserName] ?? browserName;
  return `It looks like you're using ${label} on ${platformLabel}.`;
}

export function getRecommendationsFor(browser: BrowserInfo): SupportedBrowserKey[] {
  const safeBrowser = toSafeBrowserInfo(browser);
  // Snapshot, not the exported array itself — a mounted panel (or a stale caller) mutating the
  // returned list must not corrupt recommendations for every later panel in the same page lifetime.
  return [...PLATFORM_RECOMMENDATIONS[safeBrowser.platform]];
}

// The warning dialog applies to mobile platforms only; desktop browsers handle
// large uploads without the iOS-Safari memory-pressure crashes that motivate
// the threshold.
export type MobilePlatform = Exclude<Platform, "desktop">;

// Per-platform file-size defaults. iOS Safari is the tighter constraint —
// pages can crash without a catchable exception when memory pressure spikes —
// so its threshold is lower.
const DEFAULT_LARGE_FILE_WARNING_BYTES: Record<MobilePlatform, number> = {
  ios: 1024 * 1024 * 1024, // 1 GB
  ipados: 1024 * 1024 * 1024, // 1 GB - same WebKit engine/memory-pressure profile as iOS
  android: Math.floor(1.5 * 1024 * 1024 * 1024), // 1.5 GB
};

const DEFAULT_LARGE_FILE_WARNING_LABEL: Record<MobilePlatform, string> = {
  ios: "1 GB",
  ipados: "1 GB",
  android: "1.5 GB",
};

// Lower environments (dev/testing) set VITE_LARGE_FILE_WARNING_MB to a small
// value so the dialog can be exercised without sourcing GB-sized files.
//
// Both env sources are checked because contact-admin injects env vars at
// runtime via `window.env` (from public/config/env.json — generated by
// create-environment-json.js), while contact-async exposes them via
// import.meta.env. window.env wins when both are present so a runtime hot-swap
// of env.json is reflected without rebuilding.
function getOverrideBytes(): number | null {
  const fromWindow = (window as { env?: Record<string, string | undefined> })
    .env?.VITE_LARGE_FILE_WARNING_MB;
  const fromImportMeta = import.meta.env?.VITE_LARGE_FILE_WARNING_MB;
  const overrideMb = Number(fromWindow ?? fromImportMeta);
  if (Number.isFinite(overrideMb) && overrideMb > 0) {
    return overrideMb * 1024 * 1024;
  }
  return null;
}

export function getLargeFileWarningBytes(platform: MobilePlatform): number {
  return getOverrideBytes() ?? DEFAULT_LARGE_FILE_WARNING_BYTES[platform];
}

export function getLargeFileWarningLabel(platform: MobilePlatform): string {
  const override = getOverrideBytes();
  if (override !== null) {
    return `${(override / 1024 / 1024).toFixed(0)} MB`;
  }
  return DEFAULT_LARGE_FILE_WARNING_LABEL[platform];
}

export function findLargeFiles(files: File[], thresholdBytes: number): File[] {
  return files.filter((file) => file.size > thresholdBytes);
}

function formatFileSize(bytes: number): string {
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) {
    return `${gb.toFixed(2)} GB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(0)} MB`;
}

export function buildLargeFileWarningMessage(
  largeFiles: File[],
  platform: MobilePlatform,
): string {
  const fileLimit = getLargeFileWarningLabel(platform);

  if (largeFiles.length === 1) {
    const sizeOfFile = formatFileSize(largeFiles[0].size);
    return (
      `The file you are trying to upload, ${largeFiles[0].name}, is ${sizeOfFile}. ` +
      `We recommend uploads be under ${fileLimit} to guarantee they can be uploaded to LiveSwitch. ` +
      `We are currently working on improving this process for you. ` +
      `Would you like to continue to try to upload this file?`
    );
  }

  return (
    `${largeFiles.length} of the files you are trying to upload exceed ${fileLimit}. ` +
    `We recommend uploads be under ${fileLimit} to guarantee they can be uploaded to LiveSwitch. ` +
    `We are currently working on improving this process for you. ` +
    `Would you like to continue to try to upload these files?`
  );
}
